import BaseService from './BaseService'
import { IProduct, IProductTag, IProductPagedData } from '../interfaces/product'
import { ICategory, ICategoryPagedData } from '../interfaces/category'
import { IManufacturer, IManufacturerPagedData } from '../interfaces/manufacturer'
import { groupBy } from 'lodash';
import { GetPricetag } from '../constants/Utils';

class ProductService extends BaseService {
  constructor() {
    super('product')
  }

  fetchByBrand(brandId: string): Promise<IProduct[]> {
    const formData = new FormData()
    formData.append('brand_id', brandId)

    return new Promise((resolve, reject) => {
      const cacheKey = `products_${brandId}`
      const cachedData = this.cache.get(cacheKey)
      if (cachedData) {
        resolve(cachedData)
      } else {
        try {
          this.http.post(`product/read_by_brand`, formData).then((data: any) => {
            if (data.code === 200) {
              this.cache.set(cacheKey, data.data)
              resolve(data.data)
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchByManufacturer(mftrId: string): Promise<IProduct[]> {
    const formData = new FormData()
    formData.append('manufacturer_id', mftrId)

    return new Promise((resolve, reject) => {
      const cacheKey = `products_${mftrId}`
      const cachedData = this.cache.get(cacheKey)
      if (cachedData) {
        resolve(cachedData)
      } else {
        try {
          this.http
            .post(`product/read_by_manufacturer`, formData)
            .then((data: any) => {
              if (data.code === 200) {
                this.cache.set(cacheKey, data.data)
                resolve(data.data)
              } else reject({ message: data.message })
            })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchAvailable(locationId: string, page: number, limit: number): Promise<IProduct[] | IProductPagedData[]> {
    const formData = new FormData()
    formData.append('location_id', locationId)
    formData.append('page', `${page}`)
    formData.append('limit', `${limit}`)

    return new Promise(async (resolve, reject) => {
      if (page === 0) {
        reject({ message: 'An unexpected error occured!' })
      }
      const cacheKey = `products_available_${locationId}_${page}_${limit}`;
      let cachedData = await this.cache.get(cacheKey)

      if (cachedData) {
        resolve(cachedData)
      } else {
        try {
          this.http.post(`product/read_available`, formData).then((data: any) => {
            if (data.code === 200) {
              this.cache.set(cacheKey, data.data)
              resolve(data.data)
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchAvailableByBrand(brandId: string, locationId: string, page: number, limit: number): Promise<IProductPagedData> {
    const formData = new FormData()
    formData.append('brand_id', brandId)
    formData.append('location_id', locationId)
    formData.append('page', `${page}`)
    formData.append('limit', `${limit}`)

    return new Promise(async (resolve, reject) => {
      if (page === 0) {
        reject({ message: 'Page not specified.' })
      }
      const cacheKey = `brand_product_${brandId}_${locationId}_${page}_${limit}`;
      let cachedData = await this.cache.get(cacheKey)

      if (cachedData) {
        resolve(cachedData)
      } else {
        try {
          this.http.post(`product/read_available_by_brand`, formData).then((data: any) => {
            if (data.code === 200) {
              this.cache.set(cacheKey, data.data)
              resolve(data.data)
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchAvailableByManufacturer(manufacturerId: string, locationId: string, page: number, limit: number): Promise<IProductPagedData> {
    const formData = new FormData()
    formData.append('manufacturer_id', manufacturerId)
    formData.append('location_id', locationId)
    formData.append('page', `${page}`)
    formData.append('limit', `${limit}`)

    return new Promise(async (resolve, reject) => {
      if (page === 0) {
        reject({ message: 'Page not specified.' })
      }
      const cacheKey = `manufacturer_product_${manufacturerId}_${page}_${limit}`;
      let cachedData = await this.cache.get(cacheKey)

      if (cachedData) {
        resolve(cachedData)
      } else {
        try {
          this.http.post(`product/read_available_by_manufacturer`, formData).then(async (data: any) => {
            if (data.code === 200) {
              data.data.data = data.data.data.filter((product: IProduct) => {
                let pricetag = GetPricetag(product)
                if (pricetag && product?.status === 'active') {
                  product['pricetag'] = pricetag
                  return product
                }
                return null
              })

              if (data.data) {
                this.cache.set(cacheKey, data.data)
                resolve(data.data)
              } else {
                reject({ message: 'No data found.' })
              }
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchAvailableByCategory(categoryId: string, locationId: string, page: number, limit: number): Promise<IProductPagedData> {
    const formData = new FormData()
    formData.append('_id', categoryId)
    formData.append('location_id', locationId)
    formData.append('page', `${page}`)
    formData.append('limit', `${limit}`)

    return new Promise(async (resolve, reject) => {
      if (page === 0) {
        reject({ message: 'Page not specified.' })
      }
      const cacheKey = `category_product_${categoryId}_${locationId}_${page}_${limit}`;
      let cachedData = await this.cache.get(cacheKey)

      if (cachedData) {
        resolve(cachedData)
      } else {
        try {
          this.http.post(`category/read_products_paginated`, formData).then((data: any) => {
            if (data.code === 200) {
              if (data.data) {
                this.cache.set(cacheKey, data.data)
                resolve(data.data)
              } else {
                reject({ message: 'No data found.' })
              }
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchAvailableCategories(locationId: string, page: number, limit: number): Promise<ICategory[] | ICategoryPagedData[]> {
    const formData = new FormData()
    formData.append('location_id', locationId)
    formData.append('page', `${page}`)
    formData.append('limit', `${limit}`)

    return new Promise(async (resolve, reject) => {
      if (page === 0) {
        reject({ message: 'An unexpected error occured!' })
      }
      const cacheKey = `categories_available_${locationId}_${page}_${limit}`;
      let cachedData = await this.cache.get(cacheKey)

      if (cachedData) {
        resolve(cachedData)
      } else {
        try {
          this.http.post(`category/read_available`, formData).then((data: any) => {
            if (data.code === 200) {
              // drop products property for storage optimization
              data.data.data = data.data.data.map((category: ICategory) => {
                delete category.products
                return category
              })
              this.cache.set(cacheKey, data.data)
              resolve(data.data)
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchAllAvailableCategories(locationId: string, limit: number): Promise<ICategory[] | ICategoryPagedData[]> {
    const formData = new FormData()
    formData.append('location_id', locationId)
    formData.append('page', '0')
    formData.append('limit', '0')

    return new Promise(async (resolve, reject) => {
      const cacheKey = `categories_available_${locationId}`;
      let cachedData = await this.cache.get(cacheKey)

      if (cachedData && false) {
        resolve(cachedData)
      } else {
        try {
          this.http.post(`category/read_available`, formData).then((data: any) => {
            if (data.code === 200) {
              // iterate through categories
              data.data.map((category: ICategory) => {
                  // cache categories
                  const categoryCacheKey = `category_${category._id}`
                  this.cache.set(categoryCacheKey, category)
                  
                  // paginate and cache category products
                  this.saveChunks(`category_product_${category._id}_${locationId}`, category.products, limit)
              })
              resolve(data.data)
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchAvailableManufacturers(locationId: string, page: number, limit: number): Promise<IManufacturer[] | IManufacturerPagedData[]> {
    const formData = new FormData()
    formData.append('location_id', locationId)
    formData.append('page', `${page}`)
    formData.append('limit', `${limit}`)

    return new Promise(async (resolve, reject) => {
      if (page === 0) {
        reject({ message: 'An unexpected error occured!' })
      }
      const cacheKey = `manufacturers_available_${locationId}_${page}_${limit}`;
      let cachedData = await this.cache.get(cacheKey)

      if (cachedData) {
        resolve(cachedData)
      } else {
        try {
          this.http.post(`manufacturer/read_available`, formData).then((data: any) => {
            if (data.code === 200) {
              this.cache.set(cacheKey, data.data)
              resolve(data.data)
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchAllAvailable(locationId: string): Promise<IProduct[]> {
    const formData = new FormData()
    formData.append('location_id', locationId)
    formData.append('page', '0')
    formData.append('limit', '0')

    return new Promise(async (resolve, reject) => {
      const cacheKey = `products_available`
      const cachedData = await this.cache.get(cacheKey)
      if (cachedData && false) {
        resolve(cachedData)
      } else {
        try {
          this.http.post(`product/read_available`, formData).then((data: any) => {
            if (data.code === 200) {
              resolve(data.data)
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchAndDumpAvailableChunks(locationId: string, limit: number): Promise<IProductPagedData[]> {
    return new Promise((resolve, reject) => {
      this.fetchAllAvailable(locationId)
        .then(res => {

          let manufacturerCluster: any = groupBy(res, 'manufacturer._id')
          let brandCluster: any = groupBy(res, 'brand._id')

          const productChunks = this.saveChunks(`products_available_${locationId}`, res, limit)

          for (const manufacturer_id in manufacturerCluster) {
            if (Object.prototype.hasOwnProperty.call(manufacturerCluster, manufacturer_id)) {
              const cluster = manufacturerCluster[manufacturer_id];
              this.saveChunks(`manufacturer_product_${manufacturer_id}_${locationId}`, cluster, limit)
            }
          }
          for (const brand_id in brandCluster) {
            if (Object.prototype.hasOwnProperty.call(brandCluster, brand_id)) {
              const cluster = brandCluster[brand_id];
              this.saveChunks(`brand_product_${brand_id}_${locationId}`, cluster, limit)
            }
          }

          resolve(productChunks)
        })
        .catch(err => {
          reject({ message: 'An unexpected error occured!' })
          throw err
        })
    })
  }

  updateStatus(product): Promise<any> {
    const formData = new FormData()
    formData.append('_id', product._id)
    formData.append('status', product.status)

    return new Promise((resolve, reject) => {
      try {
        this.http.post('product/set_status', formData).then(({ data }) => {
          if (data.code === 200) {
            this.cache.reset()
            resolve(data.data)
          } else reject({ message: data.message })
        })
      } catch (error) {
        reject({ message: 'An unexpected error occured!' })
        throw error
      }
    })
  }

  fetchTags(productId: string): Promise<IProductTag[]> {
    const formData = new FormData()
    formData.append('product_id', productId)

    return new Promise((resolve, reject) => {
      const cacheKey = `product_tags_${productId}`
      const cachedData = this.cache.get(cacheKey)
      if (cachedData) {
        resolve(cachedData)
      } else {
        try {
          this.http
            .post(`product_tag/read_by_product`, formData)
            .then(({ data }) => {
              if (data.code === 200) {
                this.cache.set(cacheKey, data.data)
                resolve(data.data)
              } else reject({ message: data.message })
            })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  updateTag(type, productTag: IProductTag): Promise<any> {
    this.model = 'product_tag'
    return new Promise((resolve, reject) => {
      super
      [type](productTag)
        .then((data) => resolve(data))
        .catch((error) => reject(error))
        .finally(() => {
          this.model = 'product' // Reset model
        })
    })
  }

  deleteTag(productTagId: string): Promise<any> {
    this.model = 'product_tag'
    return new Promise((resolve, reject) => {
      super
        .delete(productTagId)
        .then((data) => resolve(data))
        .catch((error) => reject(error))
        .finally(() => {
          this.model = 'product' // Reset model
        })
    })
  }

  fetchSellerProduct(sellerId, productId): Promise<any> {
    const formData = new FormData()
    formData.append('seller_id', sellerId)
    formData.append('product_id', productId)

    return new Promise((resolve, reject) => {
      const cacheKey = `seller_product_${sellerId}_${productId}`
      const cachedData = this.cache.get(cacheKey)
      if (cachedData) {
        return cachedData
      } else {
        try {
          this.http
            .post(`seller_product/read_by_seller_product`, formData)
            .then(({ data }) => {
              if (data.code === 200 && data.data.length) {
                this.cache.set(cacheKey, data.data[0])
                resolve(data.data[0])
              } else resolve()
            })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  updateSellerProduct(type, productTag: IProductTag): Promise<any> {
    this.model = 'seller_product'
    return new Promise((resolve, reject) => {
      super
      [type](productTag)
        .then((data) => resolve(data))
        .catch((error) => reject(error))
        .finally(() => {
          this.model = 'product' // Reset model
        })
    })
  }

  deleteSellerProduct(sellerProductId: string): Promise<any> {
    this.model = 'seller_product'
    return new Promise((resolve, reject) => {
      super
        .delete(sellerProductId)
        .then((data) => resolve(data))
        .catch((error) => reject(error))
        .finally(() => {
          this.model = 'product' // Reset model
        })
    })
  }

  fetchHistory(): Promise<any> {
    const formData = new FormData()
    formData.append('as_csv', 'false')

    return new Promise((resolve, reject) => {
      const cacheKey = `products_history`
      const cachedData = this.cache.get(cacheKey)
      if (cachedData) {
        return cachedData
      } else {
        try {
          this.http.post(`product/history`, formData).then(({ data }) => {
            if (data.code === 200) {
              const products = Object.values(data.data)
              this.cache.set(cacheKey, products)
              resolve(products)
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }
}

export default ProductService
